import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Search, Mail, Phone, Calendar as CalendarIcon, MessageSquare, Bell, CheckCircle, X, Pencil, Tag, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AddLeadModal = ({ onClose, onAdd, employees, packages }) => {
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", status: "Warm", assigned: "Unassigned", date: new Date().toISOString().split('T')[0], product: "", source: "Walk-in" });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-lg relative overflow-y-auto max-h-[90vh] shadow-2xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-text_primary mb-6">Add Lead</h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Date</label>
              <input type="date" value={formData.date} onChange={e=>setFormData({...formData, date: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Full Name</label>
              <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Phone</label>
              <input required type="text" value={formData.phone} onChange={e=>setFormData({...formData, phone: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Email</label>
              <input type="email" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Interested In</label>
              <select value={formData.product} onChange={e=>setFormData({...formData, product: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option value="">-- None specified --</option>
                {packages.map(pkg => <option key={pkg.id} value={pkg.name}>{pkg.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Source</label>
              <select value={formData.source} onChange={e=>setFormData({...formData, source: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option>Walk-in</option>
                <option>Instagram Ad</option>
                <option>Facebook Ad</option>
                <option>Google Ad</option>
                <option>Word of Mouth</option>
                <option>Website</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Status</label>
              <select value={formData.status} onChange={e=>setFormData({...formData, status: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option>Hot</option>
                <option>Warm</option>
                <option>Cold</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Assign To</label>
              <select value={formData.assigned} onChange={e=>setFormData({...formData, assigned: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option value="Unassigned">Unassigned</option>
                {employees.map(emp => <option key={emp.id} value={emp.name}>{emp.name}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Lead</button>
        </form>
      </div>
    </div>
  );
};

const EditLeadModal = ({ onClose, onEdit, lead, employees, packages }) => {
  const [formData, setFormData] = useState(lead);

  const handleSubmit = (e) => {
    e.preventDefault();
    onEdit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-lg relative overflow-y-auto max-h-[90vh] shadow-2xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-text_primary mb-6">Edit Lead</h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Date</label>
              <input type="date" value={formData.date || ""} onChange={e=>setFormData({...formData, date: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Full Name</label>
              <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Phone</label>
              <input required type="text" value={formData.phone} onChange={e=>setFormData({...formData, phone: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Email</label>
              <input type="email" value={formData.email} onChange={e=>setFormData({...formData, email: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Interested In</label>
              <select value={formData.product || ""} onChange={e=>setFormData({...formData, product: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option value="">-- None specified --</option>
                {packages.map(pkg => <option key={pkg.id} value={pkg.name}>{pkg.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Source</label>
              <select value={formData.source || "Walk-in"} onChange={e=>setFormData({...formData, source: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option>Walk-in</option>
                <option>Instagram Ad</option>
                <option>Facebook Ad</option>
                <option>Google Ad</option>
                <option>Word of Mouth</option>
                <option>Website</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Status</label>
              <select value={formData.status} onChange={e=>setFormData({...formData, status: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option>Hot</option>
                <option>Warm</option>
                <option>Cold</option>
                <option>Converted</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Assign To</label>
              <select value={formData.assigned} onChange={e=>setFormData({...formData, assigned: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option value="Unassigned">Unassigned</option>
                {employees.map(emp => <option key={emp.id} value={emp.name}>{emp.name}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Changes</button>
        </form>
      </div>
    </div>
  );
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [packages, setPackages] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [activeLead, setActiveLead] = useState(null);
  
  const [showModal, setShowModal] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [reminderData, setReminderData] = useState({ date: "", text: "" });

  const fetchLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/leads`);
      setLeads(res.data);
      const resHR = await axios.get(`${BACKEND_URL}/api/hr`);
      setEmployees(resHR.data);
      const pkgRes = await axios.get(`${BACKEND_URL}/api/packages`);
      setPackages(pkgRes.data);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const addNote = async (leadId) => {
    if(!noteText.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/leads/${leadId}/notes`, { note: noteText });
      fetchLeads();
      setNoteText("");
    } catch(e) { console.error(e); }
  };

  const addReminder = async (leadId) => {
    if(!reminderData.date || !reminderData.text) return;
    try {
      await axios.post(`${BACKEND_URL}/api/leads/${leadId}/reminders`, reminderData);
      fetchLeads();
      setReminderData({ date: "", text: "" });
    } catch(e) { console.error(e); }
  };

  const completeReminder = async (leadId, reminderId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/leads/${leadId}/reminders/${reminderId}/complete`);
      fetchLeads();
    } catch(e) { console.error(e); }
  };

  const handleCreateLead = async (formData) => {
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, formData);
      setShowModal(false);
      fetchLeads();
    } catch(e) { console.error(e); }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/update/leads/${data.id}`, data);
      setShowEdit(null);
      fetchLeads();
    } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
  };

  const handleDelete = async (id) => {
    if(window.confirm("Delete this lead permanently?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/delete/leads/${id}`);
        if(activeLead === id) setActiveLead(null);
        fetchLeads();
      } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
    }
  };

  const getPendingRemindersCount = (lead) => {
    return lead.reminders?.filter(r => !r.completed)?.length || 0;
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 relative">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Leads</h1>
          <p className="text-text_secondary text-sm">Track enquiries, follow-ups and conversions.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="bg-primary text-[#111827] px-6 py-3.5 rounded-xl font-bold hover:bg-primary_hover transition-colors flex items-center gap-2 shadow-md">
          <Plus size={20} /> Add Lead
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-5">
          {leads.length === 0 && <p className="text-text_secondary italic">No leads yet.</p>}
          {leads.map((l) => (
            <div key={l.id} className={`bg-card_bg border rounded-2xl p-6 shadow-sm cursor-pointer transition-all duration-300 relative group hover:-translate-y-1 ${activeLead === l.id ? 'border-primary bg-primary/5 shadow-glow' : 'border-border_soft hover:border-primary/40'}`} onClick={() => setActiveLead(l.id)}>
              <div className="absolute top-4 right-4 flex gap-2 opacity-0 md:opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button onClick={(e) => { e.stopPropagation(); setShowEdit(l); }} className="p-2 bg-card_bg border border-border_soft/50 rounded-lg text-text_secondary hover:text-gold transition-colors" title="Edit"><Pencil size={16}/></button>
                <button onClick={(e) => { e.stopPropagation(); handleDelete(l.id); }} className="p-2 bg-card_bg border border-border_soft/50 rounded-lg text-text_secondary hover:text-accent_danger transition-colors" title="Delete"><Trash2 size={16}/></button>
              </div>
              <div className="flex justify-between items-start mb-4 pr-24">
                <h3 className="text-text_primary font-bold text-xl flex items-center gap-3 tracking-tight">
                  {l.name}
                  {getPendingRemindersCount(l) > 0 && <span className="bg-accent_danger w-2.5 h-2.5 rounded-full inline-block shadow-[0_0_10px_rgba(248,113,113,0.8)]" title="Pending Action Required"></span>}
                </h3>
                <span className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold rounded-lg border ${l.status==='Converted' ? 'bg-accent_success/10 border-accent_success/20 text-accent_success' : 'bg-primary/10 border-primary/20 text-gold'}`}>{l.status}</span>
              </div>
              <div className="flex gap-4 text-text_secondary text-sm flex-wrap items-center bg-card_bg/30 p-3 rounded-xl border border-border_soft/20">
                <span className="flex items-center gap-1.5"><Phone size={14} className="opacity-70"/> {l.phone}</span>
                <span className="flex items-center gap-1.5"><CalendarIcon size={14} className="opacity-70"/> {l.date}</span>
                {l.product && <span className="flex items-center gap-1.5 text-gold"><Tag size={14} className="opacity-70"/> {l.product}</span>}
                <span className="flex items-center gap-1.5"><MessageSquare size={14} className="opacity-70"/> {l.notes?.length || 0} Notes</span>
                {getPendingRemindersCount(l) > 0 && (
                  <span className="flex items-center gap-1.5 text-accent_danger font-bold"><Bell size={14}/> {getPendingRemindersCount(l)} Reminders</span>
                )}
                {l.assigned !== "Unassigned" && <span className="flex items-center gap-1.5 text-text_primary font-medium bg-card_bg px-2.5 py-1 rounded-md border border-border_soft/40 text-xs ml-auto">{l.assigned}</span>}
              </div>
            </div>
          ))}
        </div>

        {activeLead && (
          <div className="bg-card_bg border border-border_soft rounded-2xl shadow-sm h-fit sticky top-6 flex flex-col max-h-[85vh] overflow-hidden">
            <div className="p-7 border-b border-border_soft/40 bg-card_bg/40">
              <h3 className="text-2xl font-serif text-text_primary tracking-tight mb-1">{leads.find(l => l.id === activeLead)?.name}</h3>
              <p className="text-gold text-xs uppercase tracking-widest font-bold">Details & Follow-ups</p>
            </div>
            
            <div className="p-7 overflow-y-auto space-y-8 flex-1 custom-scrollbar">
              {/* Reminders Section */}
              <div className="bg-main_bg p-5 rounded-2xl border border-border_soft/20">
                <h4 className="text-sm font-bold text-text_primary mb-4 uppercase tracking-widest flex items-center gap-2.5"><Bell size={18} className="text-gold"/> Follow-up Reminders</h4>
                <div className="space-y-3 mb-5">
                  {leads.find(l => l.id === activeLead)?.reminders?.filter(r => !r.completed).map((r, idx) => (
                    <div key={`rem-${idx}`} className="bg-accent_danger/10 p-4 rounded-xl border border-accent_danger/20 flex justify-between items-start group">
                      <div>
                        <p className="text-text_primary text-sm font-medium mb-1.5">{r.text}</p>
                        <p className="text-[10px] uppercase font-bold text-accent_danger flex items-center gap-1.5"><CalendarIcon size={12}/> Due: {r.date}</p>
                      </div>
                      <button onClick={() => completeReminder(activeLead, r.id)} className="text-text_secondary hover:text-accent_success transition-colors opacity-70 group-hover:opacity-100" title="Mark done"><CheckCircle size={20}/></button>
                    </div>
                  ))}
                  {leads.find(l => l.id === activeLead)?.reminders?.filter(r => !r.completed).length === 0 && <p className="text-text_secondary/70 text-xs italic">No pending reminders.</p>}
                </div>
                
                <div className="bg-card_bg/60 p-4 rounded-xl border border-border_soft/30 shadow-inner">
                  <div className="flex gap-3 flex-col sm:flex-row">
                    <input type="date" value={reminderData.date} onChange={e=>setReminderData({...reminderData, date: e.target.value})} className="bg-main_bg border border-border_soft rounded-lg p-2.5 text-sm text-text_primary focus:border-primary shrink-0 outline-none" />
                    <input type="text" placeholder="Reminder note..." value={reminderData.text} onChange={e=>setReminderData({...reminderData, text: e.target.value})} className="flex-1 bg-main_bg border border-border_soft rounded-lg p-2.5 text-sm text-text_primary focus:border-primary outline-none" />
                    <button onClick={() => addReminder(activeLead)} className="bg-primary text-[#111827] px-5 py-2.5 rounded-lg font-bold text-sm shrink-0 whitespace-nowrap hover:bg-primary_hover transition-colors shadow-sm">Add</button>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div>
                <h4 className="text-sm font-bold text-text_primary mb-4 uppercase tracking-widest flex items-center gap-2.5"><MessageSquare size={18} className="text-gold"/> Notes</h4>
                <div className="space-y-4 mb-5 max-h-64 overflow-y-auto pr-2">
                  {leads.find(l => l.id === activeLead)?.notes?.length > 0 ? 
                    leads.find(l => l.id === activeLead).notes.map((n, idx) => (
                      <div key={`note-${idx}`} className="bg-card_bg/50 p-4 rounded-xl border border-border_soft/30 text-sm relative">
                        <span className="absolute -left-1.5 top-5 w-3 h-3 bg-primary rounded-full border-2 border-background_card"></span>
                        <p className="text-text_primary mb-2 ml-2 leading-relaxed">{typeof n === 'object' ? n.text : n}</p>
                        <span className="text-[10px] uppercase tracking-widest font-bold text-text_secondary/60 ml-2">{typeof n === 'object' ? n.date : ''}</span>
                      </div>
                    ))
                   : <p className="text-text_secondary/70 text-xs italic">No notes yet.</p>}
                </div>
                <div className="flex gap-3">
                  <input 
                    type="text" 
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note..." 
                    className="flex-1 bg-card_bg/50 border border-border_soft rounded-xl p-3 text-sm text-text_primary focus:outline-none focus:border-primary shadow-inner"
                  />
                  <button onClick={() => addNote(activeLead)} className="bg-primary text-[#111827] px-6 py-3 rounded-xl font-bold text-sm hover:bg-primary_hover transition-colors shadow-sm">Add</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && <AddLeadModal packages={packages} employees={employees} onClose={() => setShowModal(false)} onAdd={handleCreateLead} />}
      {showEdit && <EditLeadModal lead={showEdit} packages={packages} employees={employees} onClose={() => setShowEdit(null)} onEdit={handleEdit} />}
    </div>
  );
}
